/**
 * Taking something off a bill a waiter already placed.
 *
 * The cart's discount panel decides a discount before an order exists; this one
 * changes a bill that is already on the floor, unpaid, when the customer asks
 * for something off at the counter. It sends the discount to the order, which
 * re-prices — the tax follows the smaller amount and the total drops — so the
 * printed bill and the takings both move with it.
 *
 * Past the cashier's limit it is no longer their decision, and a manager's PIN
 * is asked for in place — the same guarded box the till discount uses, so the
 * figure the cashier typed is not lost when a manager reaches over.
 */
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { ManagerApproval } from './ManagerApproval.jsx';
import { needsApproval } from './approvalCodes.js';

const field =
  'h-11 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

export function BillDiscount({ busy, error, onApply, onCancel }) {
  const [kind, setKind] = useState('percentage');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');

  const typed = Number(value);
  const valid = Number.isFinite(typed) && typed > 0;

  const discount = () => ({ type: kind, value: typed, reason: reason.trim() || undefined });

  return (
    <div className="rounded-xl border border-rule bg-surface p-4">
      <h3 className="mb-3 font-medium text-ink">Take something off</h3>

      <div className="flex flex-wrap gap-2">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          aria-label="Discount kind"
          className={field}
        >
          <option value="percentage">Percent</option>
          <option value="fixed">Rupees</option>
        </select>

        <input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label="Discount amount"
          className={`${field} w-24 text-right tabular-nums`}
        />

        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why"
          aria-label="Reason"
          className={`${field} min-w-0 flex-1`}
        />
      </div>

      {!valid && value !== '' && (
        <p className="mt-2 text-sm text-warn">A discount has to be more than nothing.</p>
      )}

      {needsApproval(error) ? (
        <ManagerApproval
          error={error}
          busy={busy}
          onApprove={(approval) => onApply(discount(), approval)}
        />
      ) : (
        error && (
          <p role="alert" className="mt-2 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">
            {error.message}
          </p>
        )
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        {!needsApproval(error) && (
          <Button onClick={() => onApply(discount())} busy={busy} disabled={!valid}>
            Apply discount
          </Button>
        )}
      </div>
    </div>
  );
}
